import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
  LocationOn,
  AttachMoney,
  CalendarToday,
  Person,
  SmokingRooms,
  Pets,
  VolumeOff,
  School,
} from '@mui/icons-material';
import { RoommatePost, User } from '../../types';
import { initials } from '../../utils/strings';
import { apiCreateRoommateListing, apiListRoommates, apiGetOrCreateThreadWith, apiGetRoommateListing, apiUpdateRoommateListing, apiDeleteRoommateListing } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const AVATAR_GRAD = 'linear-gradient(140deg,#FFC24B 0%,#FF7A36 60%,#FF5B2E 100%)';

type BackendRoommate = {
  id: string;
  author: { id: string; displayName?: string | null; firstName?: string | null; lastName?: string | null; profileImage?: string | null };
  title: string;
  description: string;
  location: string;
  moveInDate: string | null;
  budgetMin?: number | null;
  budgetMax?: number | null;
  budgetDisplay: string;
  contactInfo?: string | null;
  smokingAllowed: boolean;
  petsAllowed: boolean;
  quietPreferred: boolean;
  lookingFor?: 'male' | 'female' | 'any';
  createdAt: string;
};

const RoommateBoard: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPost, setSelectedPost] = useState<RoommatePost | null>(null);
  const [selectedDetails, setSelectedDetails] = useState<{ budgetDisplay?: string; contactInfo?: string } | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newPost, setNewPost] = useState({
    title: '',
    description: '',
    location: '',
    moveInDate: '',
    budget: '',
    contactInfo: '',
    smokingAllowed: false,
    petsAllowed: false,
    quietPreferred: false,
    lookingFor: 'any' as 'male' | 'female' | 'any',
  });
  const navigate = useNavigate();
  const [items, setItems] = useState<RoommatePost[]>([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState({
    title: '',
    description: '',
    location: '',
    moveInDate: '',
    budget: '',
    contactInfo: '',
    smokingAllowed: false,
    petsAllowed: false,
    quietPreferred: false,
    lookingFor: 'any' as 'male' | 'female' | 'any',
  });
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [createError, setCreateError] = useState<string>('');
  const [editError, setEditError] = useState<string>('');
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    minBudget: '',
    maxBudget: '',
    moveInFrom: '',
    moveInTo: '',
    lookingFor: 'all' as 'all' | 'male' | 'female' | 'any',
    smoking: 'all' as 'all' | 'yes' | 'no',
    pets: 'all' as 'all' | 'yes' | 'no',
    quiet: 'all' as 'all' | 'yes' | 'no',
  });
  const [draftFilters, setDraftFilters] = useState(filters);
  const abortRef = useRef<AbortController | null>(null);

  const debouncedSearch = useDebounce(searchTerm, 300);

  function useDebounce<T>(value: T, delay = 300) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
      const id = setTimeout(() => setDebounced(value), delay);
      return () => clearTimeout(id);
    }, [value, delay]);
    return debounced;
  }

  useEffect(() => {
    // initial load or q change
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    apiListRoommates(debouncedSearch || undefined, undefined, 20, ac.signal)
      .then((resp) => {
        const mapped = (resp.items as BackendRoommate[]).map((r) => {
          const m = mapBackendToUi(r);
          (m as any).budgetDisplay = r.budgetDisplay;
          return m;
        });
        setItems(mapped);
        setCursor(resp.nextCursor ?? null);
        setHasMore(!!resp.nextCursor);
      })
      .catch((e) => {
        if (e?.name === 'AbortError') return;
        console.error('Failed to load roommates:', e);
        setItems([]);
        setCursor(null);
        setHasMore(false);
      })
      .finally(() => {
        if (!ac.signal.aborted) {
          setLoading(false);
          setHasLoadedOnce(true);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const loadMore = () => {
    if (!hasMore || loading) return;
    const ac = new AbortController();
    apiListRoommates(debouncedSearch || undefined, cursor || undefined, 20, ac.signal)
      .then((resp) => {
        const mapped = (resp.items as BackendRoommate[]).map((r) => {
          const m = mapBackendToUi(r);
          (m as any).budgetDisplay = r.budgetDisplay;
          return m;
        });
        setItems((prev) => [...prev, ...mapped]);
        setCursor(resp.nextCursor ?? null);
        setHasMore(!!resp.nextCursor);
      })
      .catch((e) => {
        if (e?.name === 'AbortError') return;
        console.error('Failed to load more roommates:', e);
      });
  };

  function mapBackendToUi(p: BackendRoommate): RoommatePost {
    return {
      id: p.id,
      authorId: p.author.id,
      author: {
        id: p.author.id,
        email: '',
        firstName: p.author.firstName || '',
        lastName: p.author.lastName || '',
        displayName: p.author.displayName || `${p.author.firstName || ''} ${p.author.lastName || ''}`.trim(),
        profilePicture: p.author.profileImage || undefined,
        major: '',
        year: 0,
        hometown: '',
        interests: [],
        clubs: [],
        courses: [],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      title: p.title,
      description: p.description,
      budget: { min: p.budgetMin || 0, max: p.budgetMax || 0 },
      location: p.location || '',
      moveInDate: p.moveInDate ? new Date(p.moveInDate) : new Date(p.createdAt),
      preferences: {
        gender: p.lookingFor || 'any',
        smoking: p.smokingAllowed,
        pets: p.petsAllowed,
        quiet: p.quietPreferred,
      },
      contactInfo: p.contactInfo || '',
      createdAt: new Date(p.createdAt),
      updatedAt: new Date(p.createdAt),
    };
  }

  const filteredPosts = items.filter((post) => {
    const budgetValue = post.budget.min || post.budget.max || 0;
    const minBudget = filters.minBudget ? Number(filters.minBudget) : null;
    const maxBudget = filters.maxBudget ? Number(filters.maxBudget) : null;
    if (minBudget !== null && Number.isFinite(minBudget) && budgetValue < minBudget) return false;
    if (maxBudget !== null && Number.isFinite(maxBudget) && budgetValue > maxBudget) return false;

    const moveInTs = post.moveInDate.getTime();
    if (filters.moveInFrom) {
      const fromTs = new Date(`${filters.moveInFrom}T00:00:00`).getTime();
      if (!Number.isNaN(fromTs) && moveInTs < fromTs) return false;
    }
    if (filters.moveInTo) {
      const toTs = new Date(`${filters.moveInTo}T23:59:59`).getTime();
      if (!Number.isNaN(toTs) && moveInTs > toTs) return false;
    }

    if (filters.lookingFor !== 'all' && post.preferences.gender !== filters.lookingFor) return false;

    if (filters.smoking !== 'all') {
      const smokingOn = !!post.preferences.smoking;
      if (filters.smoking === 'yes' && !smokingOn) return false;
      if (filters.smoking === 'no' && smokingOn) return false;
    }
    if (filters.pets !== 'all') {
      const petsOn = !!post.preferences.pets;
      if (filters.pets === 'yes' && !petsOn) return false;
      if (filters.pets === 'no' && petsOn) return false;
    }
    if (filters.quiet !== 'all') {
      const quietOn = !!post.preferences.quiet;
      if (filters.quiet === 'yes' && !quietOn) return false;
      if (filters.quiet === 'no' && quietOn) return false;
    }

    return true;
  });

  const activeFilterCount = [
    !!filters.minBudget,
    !!filters.maxBudget,
    !!filters.moveInFrom,
    !!filters.moveInTo,
    filters.lookingFor !== 'all',
    filters.smoking !== 'all',
    filters.pets !== 'all',
    filters.quiet !== 'all',
  ].filter(Boolean).length;
  const { user } = useAuth();
  const isOwner = selectedPost && user ? (selectedPost.author.id === user.id) : false;

  const handlePostClick = async (post: RoommatePost) => {
    setSelectedPost(post);
    setSelectedDetails(null);
    setIsDialogOpen(true);
    try {
      const d = await apiGetRoommateListing(post.id);
      setSelectedDetails({ budgetDisplay: d.budgetDisplay, contactInfo: d.contactInfo || '' });
    } catch (e) {
      console.error('Failed to load listing details', e);
    }
  };

  const canSubmit = !!(newPost.title && newPost.description && newPost.location && newPost.moveInDate && newPost.budget && newPost.contactInfo && Number(newPost.budget) >= 0);
  const canSaveEdit = !!(editValues.title && editValues.description && editValues.location && editValues.moveInDate && editValues.budget && editValues.contactInfo && Number(editValues.budget) >= 0);

  function parseErrorMessage(error: unknown): string {
    const fallback = 'Unable to save listing. Please try again.';
    if (!(error instanceof Error)) return fallback;

    const raw = String(error.message || '').trim();
    if (!raw) return fallback;

    const normalized = raw.toLowerCase();
    if (normalized.includes('moveindate cannot be in the past') || normalized.includes('move_in_date_in_past')) {
      return 'Move-in date cannot be in the past.';
    }
    if (normalized.includes('schema_outdated')) {
      return 'Backend database schema is outdated. Run backend Prisma migration and try again.';
    }

    try {
      const parsed = JSON.parse(raw) as { error?: string; message?: string; success?: boolean };
      const message = parsed.error || parsed.message;
      if (!message) return fallback;
      if (message === 'schema_outdated') return 'Backend database schema is outdated. Run backend Prisma migration and try again.';
      if (message === 'moveInDate cannot be in the past') return 'Move-in date cannot be in the past.';
      return message;
    } catch {
      return raw;
    }
  }

  function isPastDateInput(value?: string): boolean {
    if (!value) return false;
    const selected = new Date(`${value}T00:00:00`);
    if (Number.isNaN(selected.getTime())) return false;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return selected < today;
  }

  const handleCreatePost = async () => {
    setCreateError('');
    if (isPastDateInput(newPost.moveInDate)) {
      setCreateError('Move-in date cannot be in the past.');
      return;
    }
    if (Number(newPost.budget) < 0) {
      setCreateError('Budget cannot be negative.');
      return;
    }
    const payload = {
      title: newPost.title,
      description: newPost.description,
      location: newPost.location,
      // Send raw YYYY-MM-DD (backend validator converts to Date)
      moveInDate: newPost.moveInDate || '',
      budget: Number(newPost.budget),
      contactInfo: newPost.contactInfo,
      smokingAllowed: newPost.smokingAllowed,
      petsAllowed: newPost.petsAllowed,
      quietPreferred: newPost.quietPreferred,
      lookingFor: newPost.lookingFor,
    };
    try {
      const created = await apiCreateRoommateListing(payload);
  // Optimistically add to top
  const mapped = mapBackendToUi(created as any as BackendRoommate);
  (mapped as any).budgetDisplay = (created as any).budgetDisplay;
      setItems((prev) => [mapped, ...prev]);
      setIsCreateDialogOpen(false);
      setNewPost({ title: '', description: '', location: '', moveInDate: '', budget: '', contactInfo: '', smokingAllowed: false, petsAllowed: false, quietPreferred: false, lookingFor: 'any' });
      setCreateError('');
    } catch (e) {
      setCreateError(parseErrorMessage(e));
      console.error('Create roommate post failed:', e);
    }
  };

  function toDateInputValue(d: Date): string {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function openEditFromSelected() {
    if (!selectedPost) return;
    const d = selectedDetails;
    setEditValues({
      title: selectedPost.title,
      description: selectedPost.description,
      location: selectedPost.location,
      moveInDate: toDateInputValue(selectedPost.moveInDate),
      budget: String(selectedPost.budget.min || 0),
      contactInfo: d?.contactInfo || '',
      smokingAllowed: !!selectedPost.preferences.smoking,
      petsAllowed: !!selectedPost.preferences.pets,
      quietPreferred: !!selectedPost.preferences.quiet,
      lookingFor: selectedPost.preferences.gender || 'any',
    });
    setEditId(selectedPost.id);
    setEditError('');
    setIsEditDialogOpen(true);
  }

  async function saveEdit() {
    if (!editId) return;
    setEditError('');
    if (isPastDateInput(editValues.moveInDate)) {
      setEditError('Move-in date cannot be in the past.');
      return;
    }
    if (Number(editValues.budget) < 0) {
      setEditError('Budget cannot be negative.');
      return;
    }
    try {
      const body: any = {
        title: editValues.title,
        description: editValues.description,
        location: editValues.location,
  // Keep as YYYY-MM-DD if provided
  moveInDate: editValues.moveInDate ? editValues.moveInDate : undefined,
        budget: editValues.budget ? Number(editValues.budget) : undefined,
        contactInfo: editValues.contactInfo,
        smokingAllowed: editValues.smokingAllowed,
        petsAllowed: editValues.petsAllowed,
        quietPreferred: editValues.quietPreferred,
        lookingFor: editValues.lookingFor,
      };
      const updated = await apiUpdateRoommateListing(editId, body);
      // Update list
      const mapped = mapBackendToUi(updated as any as BackendRoommate);
      (mapped as any).budgetDisplay = updated.budgetDisplay;
      setItems((prev) => prev.map((x) => (x.id === updated.id ? mapped : x)));
      // Update modal details if it's open on the same listing
      if (selectedPost && selectedPost.id === updated.id) {
        setSelectedPost(mapped);
        setSelectedDetails({ budgetDisplay: updated.budgetDisplay, contactInfo: updated.contactInfo || '' });
      }
      setIsEditDialogOpen(false);
      setEditError('');
    } catch (e) {
      setEditError(parseErrorMessage(e));
      console.error('update failed', e);
    }
  }

  async function confirmDelete(id: string) {
    if (!window.confirm('Delete this listing?')) return;
    try {
      await apiDeleteRoommateListing(id);
      setItems((prev) => prev.filter((x) => x.id !== id));
      if (selectedPost?.id === id) setIsDialogOpen(false);
    } catch (e) {
      console.error('delete failed', e);
    }
  }

  const onContact = async (listing: BackendRoommate) => {
    try {
      const thread = await apiGetOrCreateThreadWith(listing.author.id, { type: 'roommate-listing', listingId: listing.id });
      navigate(`/messages/${thread.id}`);
    } catch (e) {
      console.error('open contact failed', e);
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatLookingFor = (value?: 'male' | 'female' | 'any') => {
    if (value === 'male') return 'Male';
    if (value === 'female') return 'Female';
    return 'Anyone';
  };

  const minMoveInDate = toDateInputValue(new Date());

  function openFilters() {
    setDraftFilters(filters);
    setIsFilterDialogOpen(true);
  }

  function clearFilters() {
    const cleared = {
      minBudget: '',
      maxBudget: '',
      moveInFrom: '',
      moveInTo: '',
      lookingFor: 'all' as 'all' | 'male' | 'female' | 'any',
      smoking: 'all' as 'all' | 'yes' | 'no',
      pets: 'all' as 'all' | 'yes' | 'no',
      quiet: 'all' as 'all' | 'yes' | 'no',
    };
    setFilters(cleared);
    setDraftFilters(cleared);
  }

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / 86400000);
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1040, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
            Roommate Board
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Find your perfect roommate or post your own listing.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsCreateDialogOpen(true)}
          sx={{ whiteSpace: 'nowrap', flexShrink: 0, px: { xs: 1.5, sm: 2 } }}
        >
          Post Listing
        </Button>
      </Box>

      {/* Search */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by location, title, or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="outlined" startIcon={<FilterListIcon />} onClick={openFilters}>
          {activeFilterCount > 0 ? `Filter (${activeFilterCount})` : 'Filter'}
        </Button>
      </Box>

      {/* Roommate Posts */}
      <Grid container spacing={3}>
        {(!hasLoadedOnce || (loading && items.length === 0)) && Array.from({ length: 6 }).map((_, index) => (
          <Grid item xs={12} md={6} key={`roommate-skeleton-${index}`}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <Skeleton variant="circular" width={40} height={40} animation="wave" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Skeleton variant="text" width="65%" height={28} animation="wave" />
                    <Skeleton variant="text" width="55%" height={20} animation="wave" />
                  </Box>
                </Box>

                <Skeleton variant="text" width="95%" height={24} animation="wave" />
                <Skeleton variant="text" width="85%" height={24} animation="wave" sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <Skeleton variant="rounded" width={120} height={28} animation="wave" />
                  <Skeleton variant="rounded" width={112} height={28} animation="wave" />
                  <Skeleton variant="rounded" width={136} height={28} animation="wave" />
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Skeleton variant="rounded" width={124} height={28} animation="wave" />
                  <Skeleton variant="rounded" width={110} height={28} animation="wave" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {(hasLoadedOnce && (!loading || items.length > 0)) && filteredPosts.map((post) => (
          <Grid item xs={12} md={6} key={post.id}>
            <Card
              sx={{ height: '100%', cursor: 'pointer' }}
              onClick={() => handlePostClick(post)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <Avatar src={post.author.profilePicture || undefined} sx={{ background: AVATAR_GRAD, color: '#fff', fontWeight: 700 }}>
                    {initials(post.author.displayName || `${post.author.firstName} ${post.author.lastName}`)}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {post.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Posted by {post.author.displayName} • {formatTimeAgo(post.createdAt)}
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  {post.description.length > 150
                    ? `${post.description.substring(0, 150)}...`
                    : post.description}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <Chip
                    icon={<LocationOn />}
                    label={post.location}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    icon={<AttachMoney />}
                    label={(post as any).budgetDisplay || (post.budget.max > 0 ? `$${post.budget.min}/month` : '')}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    icon={<CalendarToday />}
                    label={`Move-in: ${formatDate(post.moveInDate)}`}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {post.preferences.smoking && (
                    <Chip icon={<SmokingRooms />} label={'Smoking allowed'} size="small" />
                  )}
                  {post.preferences.pets && (
                    <Chip icon={<Pets />} label="Pets allowed" size="small" />
                  )}
                  {post.preferences.quiet && (
                    <Chip icon={<VolumeOff />} label="Quiet preferred" size="small" />
                  )}
                  <Chip icon={<Person />} label={`Looking for: ${formatLookingFor(post.preferences.gender)}`} size="small" variant="outlined" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
        {hasMore && (
          <Button variant="outlined" onClick={loadMore} disabled={loading}>
            {loading ? 'Loading…' : 'Load more'}
          </Button>
        )}
        {hasLoadedOnce && !loading && !items.length && (
          <Typography variant="body2" color="text.secondary">No listings found.</Typography>
        )}
      </Box>

      {/* View Post Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedPost && (
          <>
            <DialogTitle>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>
                {selectedPost.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar src={selectedPost.author.profilePicture || undefined} sx={{ background: AVATAR_GRAD, color: '#fff', fontWeight: 700 }}>
                  {initials(selectedPost.author.displayName || `${selectedPost.author.firstName} ${selectedPost.author.lastName}`)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1">
                    {selectedPost.author.displayName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedPost.author.major} • Year {selectedPost.author.year}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {selectedPost.description}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Location
                  </Typography>
                  <Typography variant="body2">
                    {selectedPost.location}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    <AttachMoney sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Budget
                  </Typography>
                  <Typography variant="body2">
                    {selectedDetails?.budgetDisplay || ((selectedPost as any).budgetDisplay) || (selectedPost.budget.max > 0 ? `$${selectedPost.budget.min}/month` : '')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    <CalendarToday sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Move-in Date
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedPost.moveInDate)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    <School sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Contact
                  </Typography>
                  <Typography variant="body2">
                    {selectedDetails?.contactInfo || '—'}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" gutterBottom>
                Preferences
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedPost.preferences.smoking && (<Chip icon={<SmokingRooms />} label="Smoking allowed" size="small" />)}
                {selectedPost.preferences.pets && (<Chip icon={<Pets />} label="Pets allowed" size="small" />)}
                {selectedPost.preferences.quiet && (<Chip icon={<VolumeOff />} label="Quiet preferred" size="small" />)}
                <Chip icon={<Person />} label={`Looking for: ${formatLookingFor(selectedPost.preferences.gender)}`} size="small" variant="outlined" />
              </Box>
            </DialogContent>
            
            <DialogActions>
              <Button onClick={() => setIsDialogOpen(false)}>Close</Button>
              {isOwner && (
                <>
                  <Button variant="outlined" onClick={openEditFromSelected}>Edit</Button>
                  <Button color="error" onClick={() => confirmDelete(selectedPost.id)}>Delete</Button>
                </>
              )}
              <Button variant="contained" onClick={() => onContact((items.find(i => i.id === selectedPost.id) as any))}>Contact</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Create Post Dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false);
          setCreateError('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>Create Roommate Listing</DialogTitle>
        <DialogContent>
          {!!createError && (
            <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
              {createError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={newPost.title}
                onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={newPost.description}
                onChange={(e) => setNewPost({ ...newPost, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={newPost.location}
                onChange={(e) => setNewPost({ ...newPost, location: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Move-in Date"
                type="date"
                value={newPost.moveInDate}
                onChange={(e) => {
                  setNewPost({ ...newPost, moveInDate: e.target.value });
                  if (createError) setCreateError('');
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: minMoveInDate }}
                error={isPastDateInput(newPost.moveInDate)}
                helperText={isPastDateInput(newPost.moveInDate) ? 'Move-in date cannot be in the past.' : undefined}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Budget ($/month)"
                type="number"
                value={newPost.budget}
                onChange={(e) => setNewPost({ ...newPost, budget: e.target.value })}
                inputProps={{ min: 0 }}
                error={!!newPost.budget && Number(newPost.budget) < 0}
                helperText={!!newPost.budget && Number(newPost.budget) < 0 ? 'Budget cannot be negative.' : undefined}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="new-looking-for-label">Looking for</InputLabel>
                <Select
                  labelId="new-looking-for-label"
                  label="Looking for"
                  value={newPost.lookingFor}
                  onChange={(e) => setNewPost({ ...newPost, lookingFor: e.target.value as 'male' | 'female' | 'any' })}
                >
                  <MenuItem value="any">Anyone</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="male">Male</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormGroup>
                <FormControlLabel control={<Checkbox checked={newPost.smokingAllowed} onChange={(e) => setNewPost({ ...newPost, smokingAllowed: e.target.checked })} />} label="Smoking allowed" />
                <FormControlLabel control={<Checkbox checked={newPost.petsAllowed} onChange={(e) => setNewPost({ ...newPost, petsAllowed: e.target.checked })} />} label="Pets allowed" />
                <FormControlLabel control={<Checkbox checked={newPost.quietPreferred} onChange={(e) => setNewPost({ ...newPost, quietPreferred: e.target.checked })} />} label="Quiet environment preferred" />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contact Information"
                value={newPost.contactInfo}
                onChange={(e) => setNewPost({ ...newPost, contactInfo: e.target.value })}
                helperText="Email or phone number"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreatePost}
            disabled={!canSubmit}
          >
            Create Listing
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Post Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setEditError('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>Edit Roommate Listing</DialogTitle>
        <DialogContent>
          {!!editError && (
            <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
              {editError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Title" value={editValues.title} onChange={(e) => setEditValues({ ...editValues, title: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" multiline rows={4} value={editValues.description} onChange={(e) => setEditValues({ ...editValues, description: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Location" value={editValues.location} onChange={(e) => setEditValues({ ...editValues, location: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Move-in Date" type="date" value={editValues.moveInDate} onChange={(e) => {
                setEditValues({ ...editValues, moveInDate: e.target.value });
                if (editError) setEditError('');
              }} InputLabelProps={{ shrink: true }} inputProps={{ min: minMoveInDate }} error={isPastDateInput(editValues.moveInDate)} helperText={isPastDateInput(editValues.moveInDate) ? 'Move-in date cannot be in the past.' : undefined} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="edit-looking-for-label">Looking for</InputLabel>
                <Select
                  labelId="edit-looking-for-label"
                  label="Looking for"
                  value={editValues.lookingFor}
                  onChange={(e) => setEditValues({ ...editValues, lookingFor: e.target.value as 'male' | 'female' | 'any' })}
                >
                  <MenuItem value="any">Anyone</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="male">Male</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Budget ($/month)" type="number" value={editValues.budget} onChange={(e) => setEditValues({ ...editValues, budget: e.target.value })} inputProps={{ min: 0 }} error={!!editValues.budget && Number(editValues.budget) < 0} helperText={!!editValues.budget && Number(editValues.budget) < 0 ? 'Budget cannot be negative.' : undefined} />
            </Grid>
            <Grid item xs={12}>
              <FormGroup>
                <FormControlLabel control={<Checkbox checked={editValues.smokingAllowed} onChange={(e) => setEditValues({ ...editValues, smokingAllowed: e.target.checked })} />} label="Smoking allowed" />
                <FormControlLabel control={<Checkbox checked={editValues.petsAllowed} onChange={(e) => setEditValues({ ...editValues, petsAllowed: e.target.checked })} />} label="Pets allowed" />
                <FormControlLabel control={<Checkbox checked={editValues.quietPreferred} onChange={(e) => setEditValues({ ...editValues, quietPreferred: e.target.checked })} />} label="Quiet environment preferred" />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Contact Information" value={editValues.contactInfo} onChange={(e) => setEditValues({ ...editValues, contactInfo: e.target.value })} helperText="Email or phone number" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={!canSaveEdit}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Filters Dialog */}
      <Dialog
        open={isFilterDialogOpen}
        onClose={() => setIsFilterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>Filter Listings</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Min Budget"
                type="number"
                value={draftFilters.minBudget}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || Number(value) >= 0) {
                    setDraftFilters({ ...draftFilters, minBudget: value });
                  }
                }}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Budget"
                type="number"
                value={draftFilters.maxBudget}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || Number(value) >= 0) {
                    setDraftFilters({ ...draftFilters, maxBudget: value });
                  }
                }}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Move-in from"
                type="date"
                value={draftFilters.moveInFrom}
                onChange={(e) => setDraftFilters({ ...draftFilters, moveInFrom: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Move-in to"
                type="date"
                value={draftFilters.moveInTo}
                onChange={(e) => setDraftFilters({ ...draftFilters, moveInTo: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="filter-lookingfor-label">Looking for</InputLabel>
                <Select
                  labelId="filter-lookingfor-label"
                  label="Looking for"
                  value={draftFilters.lookingFor}
                  onChange={(e) => setDraftFilters({ ...draftFilters, lookingFor: e.target.value as 'all' | 'male' | 'female' | 'any' })}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="any">Anyone</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="male">Male</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="filter-smoking-label">Smoking allowed</InputLabel>
                <Select
                  labelId="filter-smoking-label"
                  label="Smoking allowed"
                  value={draftFilters.smoking}
                  onChange={(e) => setDraftFilters({ ...draftFilters, smoking: e.target.value as 'all' | 'yes' | 'no' })}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="filter-pets-label">Pets allowed</InputLabel>
                <Select
                  labelId="filter-pets-label"
                  label="Pets allowed"
                  value={draftFilters.pets}
                  onChange={(e) => setDraftFilters({ ...draftFilters, pets: e.target.value as 'all' | 'yes' | 'no' })}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="filter-quiet-label">Quiet preferred</InputLabel>
                <Select
                  labelId="filter-quiet-label"
                  label="Quiet preferred"
                  value={draftFilters.quiet}
                  onChange={(e) => setDraftFilters({ ...draftFilters, quiet: e.target.value as 'all' | 'yes' | 'no' })}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearFilters}>Clear</Button>
          <Button onClick={() => setIsFilterDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setFilters(draftFilters);
              setIsFilterDialogOpen(false);
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoommateBoard; 